import React, { useState, useEffect } from 'react';
import { FiX, FiDownload } from 'react-icons/fi';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { settingsAPI } from '../../services/api';
import skjLogo from '../../assets/skj_logo.jpeg';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const InvoiceModal = ({ invoice, onClose }) => {
    const [companySettings, setCompanySettings] = useState(null);
    const [settingsLoading, setSettingsLoading] = useState(true);

    useEffect(() => {
        fetchCompanySettings();
    }, []);

    const fetchCompanySettings = async () => {
        try {
            const response = await settingsAPI.get();
            setCompanySettings(response.data.data);
            console.log("Checking setting data", response.data)
        } catch (error) {
            console.error('Failed to fetch company settings:', error);
        } finally {
            setSettingsLoading(false);
        }
    };

    // Helper function to convert image URL to base64
    const getBase64FromUrl = async (url) => {
        try {
            // If already base64, return as is
            if (url && url.startsWith('data:')) {
                return url;
            }

            // Add cache-busting parameter to force fresh fetch
            const cacheBustUrl = url.includes('?')
                ? `${url}&t=${Date.now()}`
                : `${url}?t=${Date.now()}`;

            const response = await fetch(cacheBustUrl, {
                mode: 'cors',
                cache: 'no-cache'
            });
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('Error converting image to base64:', error);
            return null;
        }
    };

    // Helper function to convert number to words
    const convertNumberToWords = (num) => {
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

        if (num === 0) return 'Zero';

        const numStr = Math.floor(num).toString();
        let words = '';

        // Thousands
        if (numStr.length > 3) {
            const thousands = parseInt(numStr.slice(0, -3));
            if (thousands > 0) {
                words += convertNumberToWords(thousands) + ' Thousand ';
            }
        }

        // Hundreds
        const lastThree = parseInt(numStr.slice(-3));
        const hundreds = Math.floor(lastThree / 100);
        if (hundreds > 0) {
            words += ones[hundreds] + ' Hundred ';
        }

        // Tens and ones
        const lastTwo = lastThree % 100;
        if (lastTwo >= 10 && lastTwo < 20) {
            words += teens[lastTwo - 10] + ' ';
        } else {
            const tensDigit = Math.floor(lastTwo / 10);
            const onesDigit = lastTwo % 10;
            if (tensDigit > 0) words += tens[tensDigit] + ' ';
            if (onesDigit > 0) words += ones[onesDigit] + ' ';
        }

        // Handle "and" for proper formatting
        if (words.includes('Hundred') && (lastTwo > 0)) {
            words = words.replace('Hundred ', 'Hundred and ');
        }

        return words.trim();
    };

    useEffect(() => {
        console.log("🧾 FULL INVOICE OBJECT:", invoice);
    }, [invoice]);

    // Professional PDF Download Function - Exact match to SKJ format
    const downloadPDF = async () => {
        if (!invoice) {
            toast.error("No invoice selected!");
            return;
        }

        try {
            const toastId = toast.loading("Generating PDF...");

            // Create a temporary container for the invoice
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.left = '-9999px';
            container.style.width = '210mm'; // A4 width
            // container.style.minHeight = '297mm'; // A4 height
            container.style.backgroundColor = '#ffffff';
            container.style.fontFamily = 'Arial, sans-serif';
            container.style.color = '#000000';
            document.body.appendChild(container);

            // Calculate tax details
            const taxRate = 18; // 18% GST
            const subtotalAmount = parseFloat(invoice.subtotal || 0);
            const taxAmount = parseFloat(invoice.tax_amount || 0);
            const totalAmount = parseFloat(invoice.total_amount);
            const roundOff = (Math.round(totalAmount) - totalAmount);
            const finalTotal = Math.round(totalAmount);
            const totalQty = invoice.items?.reduce((sum, item) => sum + parseInt(item.quantity || 0), 0) || 0;
            const totalDiscountAmount = invoice.items?.reduce((sum, item) => {
                const qty = parseFloat(item.quantity || 0);
                const rate = parseFloat(item.rate || item.price || 0);
                const disc = parseFloat(item.discount || 0);
                return sum + ((qty * rate * disc) / 100);
            }, 0) || 0;

            // Get customer state - use supply_state for GST determination (Place of Supply)
            const customerState = invoice.customer_state || '';
            const placeOfSupplyState = invoice.supply_state || customerState || '';

            // State code mapping for formatting
            const stateCodeMap = {
                'jammu & kashmir': '01', 'himachal pradesh': '02', 'punjab': '03', 'chandigarh': '04',
                'uttarakhand': '05', 'haryana': '06', 'delhi': '07', 'rajasthan': '08',
                'uttar pradesh': '09', 'bihar': '10', 'sikkim': '11', 'arunachal pradesh': '12',
                'nagaland': '13', 'manipur': '14', 'mizoram': '15', 'tripura': '16',
                'meghalaya': '17', 'assam': '18', 'west bengal': '19', 'jharkhand': '20',
                'odisha': '21', 'chhattisgarh': '22', 'madhya pradesh': '23', 'gujarat': '24',
                'dadra & nagar haveli and daman & diu': '26', 'maharashtra': '27', 'karnataka': '29',
                'goa': '30', 'lakshadweep': '31', 'kerala': '32', 'tamil nadu': '33',
                'puducherry': '34', 'andaman & nicobar islands': '35', 'telangana': '36',
                'andhra pradesh': '37', 'ladakh': '38'
            };

            // Format state with code (e.g., "Delhi" -> "07-Delhi")
            const formatStateWithCode = (state) => {
                if (!state) return '';
                // If already has code format, return as is
                if (/^\d{2}-/.test(state)) return state;
                const normalizedState = state.toLowerCase().trim();
                const code = stateCodeMap[normalizedState] || '';
                return code ? `${code}-${state}` : state;
            };

            // Get formatted states
            const formattedCustomerState = formatStateWithCode(customerState);
            const formattedSupplyState = formatStateWithCode(placeOfSupplyState);

            // Determine GST type from place of supply vs company state
            // If place of supply matches company state (Delhi) -> CGST/SGST, else -> IGST
            const determineGstType = () => {
                if (invoice.gst_type) return invoice.gst_type;

                const companyStateFromSettings = companySettings?.company_state || 'Delhi';

                // Normalize states for comparison
                const normalizeState = (state) => {
                    if (!state) return '';
                    const cleaned = state.includes('-') ? state.split('-')[1].trim() : state.trim();
                    return cleaned.toLowerCase();
                };

                const normalizedSupplyState = normalizeState(placeOfSupplyState);
                const normalizedCompanyState = normalizeState(companyStateFromSettings);

                return normalizedSupplyState === normalizedCompanyState ? 'CGST_SGST' : 'IGST';
            };

            const gstType = determineGstType();
            const isIntraState = gstType === 'CGST_SGST';
            const halfTaxAmount = taxAmount / 2;

            // Build customer full address
            const customerFullAddress = [
                invoice.customer_address,
                invoice.customer_city,
                customerState,
                invoice.customer_pincode
            ].filter(Boolean).join(', ');

            // Get company details from settings (with fallbacks)
            const companyName = companySettings?.company_name || 'SKJ CREATIONS';
            const companyAddress = companySettings?.company_address || 'A-9 Flatted Factory Complex, Jhandewalan, New Delhi - 110055';
            const companyPhone = companySettings?.company_phone || '9911824992';
            const companyEmail = companySettings?.company_email || 'skj.getnoticed@gmail.com';
            const companyGst = companySettings?.company_gst || '07AEZFS6370L1ZZ';
            const companyState = companySettings?.company_state || '07-Delhi';

            // Build logo URL and convert to base64 for PDF generation
            const logoUrl = companySettings?.company_logo
                ? `${API_URL.replace('/api', '')}${companySettings.company_logo}`
                : skjLogo;

            // Convert logo to base64 to avoid CORS issues in PDF
            let companyLogoUrl = skjLogo;
            try {
                const logoBase64 = await getBase64FromUrl(logoUrl);
                if (logoBase64) {
                    companyLogoUrl = logoBase64;
                }
            } catch (err) {
                console.error('Failed to load logo, using default:', err);
            }

            // Build signature URL and convert to base64
            let signatureUrl = null;
            if (companySettings?.signature_image) {
                const sigUrl = `${API_URL.replace('/api', '')}${companySettings.signature_image}`;
                try {
                    const sigBase64 = await getBase64FromUrl(sigUrl);
                    if (sigBase64) {
                        signatureUrl = sigBase64;
                    }
                } catch (err) {
                    console.error('Failed to load signature:', err);
                }
            }

            // Bank details from settings
            const bankName = companySettings?.bank_name || 'Hdfc Bank, Pahar Ganj - New Delhi';
            const bankAccountNo = companySettings?.bank_account_no || '50200082353663';
            const bankIfsc = companySettings?.bank_ifsc || 'HDFC0000457';
            const bankAccountHolder = companySettings?.bank_account_holder || 'SKJ CREATIONS';

            // invoice title
            const invoiceTitle =
                invoice?.invoice_type && typeof invoice.invoice_type === "string"
                    ? invoice.invoice_type.replace(/_/g, " ").toUpperCase()
                    : "TAX INVOICE";

            // Terms and condition
            const defaultTerms = [
                "1. Goods once sold will not be taken back.",
                "2. Interest @18% p.a. will be charged if the payment is not made within the stipulated time.",
                '3. Subject to "Delhi" Jurisdiction only.'
            ];
            const activeTerms = (companySettings?.invoice_terms && companySettings.invoice_terms.trim() !== "")
                ? companySettings.invoice_terms.split('\n') // API text ko lines mein todein
                : defaultTerms;

            // Invoice HTML content - EXACT match to uploaded image
            container.innerHTML = `
        <div style="width: 210mm; padding: 20px; box-sizing: border-box;">

          <div style="text-align: center; font-weight: bold; font-size: 14px; margin-bottom: 5px;">${invoiceTitle} INVOICE</div>

          <div style="border: 1px solid black; display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 5px; border-bottom: 2px solid #000; padding:10px 15px">
             <div style="width: 30%;">
                 <img
                    src="${companyLogoUrl}"
                    alt="Company Logo"
                    style="width: 80px; height: 80px; border-radius: 50%; margin-left: 0px; object-fit: cover;"
                 />
             </div>

             <div style="text-align: right; width: 70%; font-size: 10px;">
                 <h2 style="margin: 0; font-size: 18px; font-weight: bold; text-transform: uppercase;">${companyName}</h2>
                 <p style="margin: 2px 0;">${companyAddress}</p>
                 <p style="margin: 2px 0;">Phone no.: ${companyPhone} Email: ${companyEmail}</p>
                 <p style="margin: 2px 0;">GSTIN: ${companyGst}, State: ${companyState}</p>
             </div>
          </div>

          <div style="display: flex; border: 1px solid #000; border-bottom: none;">
             <div style="width: 50%; background-color: #ff8c00; color: white; padding: 4px 8px; padding-bottom: 8px; font-weight: bold; font-size: 11px;">Bill To</div>
             <div style="width: 50%; background-color: #ff8c00; color: white; padding: 4px 8px; padding-bottom: 8px; font-weight: bold; font-size: 11px; text-align: right; border-left: 1px solid #000;">Invoice Details</div>
          </div>

          <div style="display: flex; border: 1px solid #000; font-size: 10px;">
             <div style="width: 50%; padding: 8px; border-right: 1px solid #000;">
                <div style="font-weight: bold; font-size: 11px; margin-bottom: 2px;">${invoice.customer_name || 'N/A'}</div>
                <div style="margin-bottom: 2px;">${customerFullAddress || invoice.customer_address || 'N/A'}</div>
                <div style="margin-bottom: 2px;">GSTIN Number: ${invoice.customer_gstin || 'N/A'}</div>
                <div>State: ${formattedCustomerState || 'N/A'}</div>
             </div>
             <div style="width: 50%; padding: 8px; text-align: right;">
                <div style="margin-bottom: 2px;">Invoice No.: <span style="font-weight: bold;">${invoice.invoice_number}</span></div>
                <div style="margin-bottom: 2px;">Date: <span style="font-weight: bold;">${new Date(invoice.invoice_date).toLocaleDateString('en-GB').replace(/\//g, '-')}</span></div>
                <div>Place of Supply: <span style="font-weight: bold;">${formattedSupplyState || formattedCustomerState || 'N/A'}</span></div>
             </div>
          </div>

          <div style="margin-top: 10px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
               <thead>
                 <tr style="background-color: #ff8c00; color: white; padding-bottom: 8px;">
                   <th style="border: 1px solid #000; padding: 5px;  padding-bottom: 8px; width: 30px; text-align: left;">#</th>
                   <th style="border: 1px solid #000; padding: 5px; padding-bottom: 8px; text-align: left;">Item name</th>
                   <th style="border: 1px solid #000; padding: 5px; padding-bottom: 8px; text-align: center;">HSN/ SAC</th>
                   <th style="border: 1px solid #000; padding: 5px; padding-bottom: 8px; text-align: center;">Quantity</th>
                   <th style="border: 1px solid #000; padding: 5px; padding-bottom: 8px; text-align: right;">Price/ Unit</th>
                   <th style="border: 1px solid #000; padding: 5px; padding-bottom: 8px; text-align: right;">Disc%</th>
                   <th style="border: 1px solid #000; padding: 5px; padding-bottom: 8px; text-align: right;">Taxable amount</th>
                   ${isIntraState ? `
                   <th style="border: 1px solid #000; padding: 5px; padding-bottom: 8px; text-align: right;">CGST</th>
                   <th style="border: 1px solid #000; padding: 5px; padding-bottom: 8px; text-align: right;">SGST</th>
                   ` : `
                   <th style="border: 1px solid #000; padding: 5px; padding-bottom: 8px; text-align: right;">IGST</th>
                   `}
                   <th style="border: 1px solid #000; padding: 5px; padding-bottom: 8px; text-align: right;">Amount</th>
                 </tr>
               </thead>
               <tbody>
                  ${invoice.items && invoice.items.length > 0 ? invoice.items.map((item, i) => {
                const rate = parseFloat(item.rate || item.price || 0);
                const qty = parseFloat(item.quantity || 0);
                const discount = parseFloat(item.discount || 0);
                const baseAmount = rate * qty;
                const discountAmount = (baseAmount * discount) / 100;
                const taxable = baseAmount - discountAmount;
                const itemTaxRate = parseFloat(item.tax_rate || taxRate);
                const tax = taxable * (itemTaxRate / 100);
                const halfTax = tax / 2;
                const total = taxable + tax;
                const itemName = item.item_name || item.product_name || item.name || 'Item';
                const halfRate = itemTaxRate / 2;
                return `
                        <tr>
                          <td style="border: 1px solid #000; padding: 5px;">${i + 1}</td>
                          <td style="border: 1px solid #000; padding: 5px;">
                             <div style="font-weight: bold;">${itemName}</div>
                             ${item.description ? `<div style="font-size: 9px;">(${item.description})</div>` : ''}
                          </td>
                          <td style="border: 1px solid #000; padding: 5px; text-align: center;">${item.hsn_sac || 'N/A'}</td>
                          <td style="border: 1px solid #000; padding: 5px; text-align: center;">${qty} ${item.unit || ''}</td>
                          <td style="border: 1px solid #000; padding: 5px; text-align: right;">₹ ${rate.toFixed(2)}</td>
                          <td style="border: 1px solid #000; padding: 5px; text-align: right;">${discount > 0 ? discount.toFixed(2) + '%' : '-'}</td>
                          <td style="border: 1px solid #000; padding: 5px; text-align: right;">₹ ${taxable.toFixed(2)}</td>
                          ${isIntraState ? `
                          <td style="border: 1px solid #000; padding: 5px; text-align: right;">
                             <div>₹ ${halfTax.toFixed(2)}</div>
                             <div style="font-size: 8px;">(${halfRate.toFixed(1)}%)</div>
                          </td>
                          <td style="border: 1px solid #000; padding: 5px; text-align: right;">
                             <div>₹ ${halfTax.toFixed(2)}</div>
                             <div style="font-size: 8px;">(${halfRate.toFixed(1)}%)</div>
                          </td>
                          ` : `
                          <td style="border: 1px solid #000; padding: 5px; text-align: right;">
                             <div>₹ ${tax.toFixed(2)}</div>
                             <div style="font-size: 8px;">(${itemTaxRate}.0%)</div>
                          </td>
                          `}
                          <td style="border: 1px solid #000; padding: 5px; text-align: right;">₹ ${total.toFixed(2)}</td>
                        </tr>
                      `;
            }).join('') : ''}
                  
                  <tr style="font-weight: bold; background-color: #fff2cc;">
                     <td colspan="2" style="border: 1px solid #000; padding: 5px;">Total</td>
                     <td style="border: 1px solid #000; padding: 5px;"></td>
                     <td style="border: 1px solid #000; padding: 5px; text-align: center;">${totalQty}</td>
                     <td style="border: 1px solid #000; padding: 5px;"></td>
                     <td style="border: 1px solid #000; padding: 5px;"></td>
                     <td style="border: 1px solid #000; padding: 5px; text-align: right;">₹ ${subtotalAmount.toFixed(2)}</td>
                     ${isIntraState ? `
                     <td style="border: 1px solid #000; padding: 5px; text-align: right;">₹ ${halfTaxAmount.toFixed(2)}</td>
                     <td style="border: 1px solid #000; padding: 5px; text-align: right;">₹ ${halfTaxAmount.toFixed(2)}</td>
                     ` : `
                     <td style="border: 1px solid #000; padding: 5px; text-align: right;">₹ ${taxAmount.toFixed(2)}</td>
                     `}
                     <td style="border: 1px solid #000; padding: 5px; text-align: right;">₹ ${totalAmount.toFixed(2)}</td>
                  </tr>
               </tbody>
            </table>
          </div>

          <div style="margin-top: 10px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
               <thead>
                 <tr style="background-color: #ff8c00; color: white;">
                   <th style="border: 1px solid #000; padding: 5px;  padding-bottom: 8px; text-align: left; width: 30%;">Tax type</th>
                   <th style="border: 1px solid #000; padding: 5px;  padding-bottom: 8px; text-align: right; width: 15%;">Taxable amount</th>
                   <th style="border: 1px solid #000; padding: 5px; padding-bottom: 8px; text-align: center; width: 10%;">Rate</th>
                   <th style="border: 1px solid #000; padding: 5px; padding-bottom: 8px; text-align: right; width: 15%;">Tax amount</th>
                   <th style="border: 1px solid #000; padding: 5px; padding-bottom: 8px; text-align: center; width: 30%;" colspan="2">Amounts</th>
                 </tr>
               </thead>
               <tbody>
                 ${isIntraState ? `
                 <tr>
                   <td style="border: 1px solid #000; padding: 5px;">CGST</td>
                   <td style="border: 1px solid #000; padding: 5px; text-align: right;">₹ ${subtotalAmount.toFixed(2)}</td>
                   <td style="border: 1px solid #000; padding: 5px; text-align: center;">9.0%</td>
                   <td style="border: 1px solid #000; padding: 5px; text-align: right;">₹ ${halfTaxAmount.toFixed(2)}</td>
                   <td style="border-left: 1px solid #000; border-bottom: 1px solid #000; padding: 5px;">Sub Total</td>
                   <td style="border-right: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; text-align: right;">₹ ${totalAmount.toFixed(2)}</td>
                 </tr>
                 <tr>
                   <td style="border: 1px solid #000; padding: 5px;">SGST</td>
                   <td style="border: 1px solid #000; padding: 5px; text-align: right;">₹ ${subtotalAmount.toFixed(2)}</td>
                   <td style="border: 1px solid #000; padding: 5px; text-align: center;">9.0%</td>
                   <td style="border: 1px solid #000; padding: 5px; text-align: right;">₹ ${halfTaxAmount.toFixed(2)}</td>
                   ${totalDiscountAmount > 0 ? `
                   <td style="border-left: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; ">Discount</td>
                   <td style="border-right: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; text-align: right; color: red;">- ₹ ${totalDiscountAmount.toFixed(2)}</td>
                 </tr>
                 <tr>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   ` : ``}
                   <td style="border-left: 1px solid #000; border-bottom: 1px solid #000; padding: 5px;">Round off</td>
                   <td style="border-right: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; text-align: right;">₹ ${roundOff.toFixed(2)}</td>
                 </tr>
                 ` : `
                 <tr>
                   <td style="border: 1px solid #000; padding: 5px;">IGST</td>
                   <td style="border: 1px solid #000; padding: 5px; text-align: right;">₹ ${subtotalAmount.toFixed(2)}</td>
                   <td style="border: 1px solid #000; padding: 5px; text-align: center;">${taxRate}.0%</td>
                   <td style="border: 1px solid #000; padding: 5px; text-align: right;">₹ ${taxAmount.toFixed(2)}</td>
                   <td style="border-left: 1px solid #000; border-bottom: 1px solid #000; padding: 5px;">Sub Total</td>
                   <td style="border-right: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; text-align: right;">₹ ${totalAmount.toFixed(2)}</td>
                 </tr>

                 ${totalDiscountAmount > 0 ? `
                 <tr>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border-left: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; ">Discount</td>
                   <td style="border-right: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; text-align: right;">- ₹ ${totalDiscountAmount.toFixed(2)}</td>
                 </tr>
                 ` : ''}

                 <tr>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border-left: 1px solid #000; border-bottom: 1px solid #000; padding: 5px;">Round off</td>
                   <td style="border-right: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; text-align: right;">₹ ${roundOff.toFixed(2)}</td>
                 </tr>
                 `}

                 ${isIntraState ? `
                 <tr>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border-left: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; font-weight: bold;">Total</td>
                   <td style="border-right: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; text-align: right; font-weight: bold;">₹ ${finalTotal.toFixed(2)}</td>
                 </tr>
                 <tr>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border-left: 1px solid #000; border-bottom: 1px solid #000; padding: 5px;">Received</td>
                   <td style="border-right: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; text-align: right;">₹ ${parseFloat(invoice.paid_amount || 0).toFixed(2)}</td>
                 </tr>
                 <tr>
                   <td style="border: 1px solid #000; padding: 5px; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-top: none;"></td>
                   <td style="border-left: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; font-weight: bold;">Balance</td>
                   <td style="border-right: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; text-align: right; font-weight: bold;">₹ ${(finalTotal - parseFloat(invoice.paid_amount || 0)).toFixed(2)}</td>
                 </tr>
                 ` : `
                 <tr>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border-left: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; font-weight: bold;">Total</td>
                   <td style="border-right: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; text-align: right; font-weight: bold;">₹ ${finalTotal.toFixed(2)}</td>
                 </tr>

                 <tr>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-bottom: none; border-top: none;"></td>
                   <td style="border-left: 1px solid #000; border-bottom: 1px solid #000; padding: 5px;">Received</td>
                   <td style="border-right: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; text-align: right;">₹ ${parseFloat(invoice.paid_amount || 0).toFixed(2)}</td>
                 </tr>

                 <tr>
                   <td style="border: 1px solid #000; padding: 5px; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-top: none;"></td>
                   <td style="border: 1px solid #000; padding: 5px; border-top: none;"></td>
                   <td style="border-left: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; font-weight: bold;">Balance</td>
                   <td style="border-right: 1px solid #000; border-bottom: 1px solid #000; padding: 5px; text-align: right; font-weight: bold;">₹ ${(finalTotal - parseFloat(invoice.paid_amount || 0)).toFixed(2)}</td>
                 </tr>
                 `}
               </tbody>
            </table>
          </div>

          <div style="margin-top: 10px; border: 1px solid #000;">
             <div style="background-color: #ff8c00; color: white; padding: 5px 8px;  padding-bottom: 8px; font-weight: bold; font-size: 11px;">Invoice Amount In Words</div>
             <div style="padding: 8px; font-size: 11px;">${convertNumberToWords(finalTotal)} Rupees only</div>
          </div>

          <div style="margin-top: 10px; border: 1px solid #000;">
             <div style="background-color: #ff8c00; color: white; padding: 5px 8px; font-weight: bold;  padding-bottom: 8px; font-size: 11px;">Payment Mode</div>
             <div style="padding: 8px; font-size: 11px;">${invoice.payment_mode || 'Credit'}</div>
          </div>

          <div style="margin-top: 10px; display: flex; border: 1px solid #000; font-size: 10px;">
    
    <div style="width: 35%; padding: 8px; border-right: 1px solid #000;">
        <div style="background-color: #ff8c00; color: white; padding: 2px 5px; font-weight: bold; padding-bottom: 8px; margin-bottom: 5px; display: inline-block;">Bank Details</div>
        <p style="margin: 2px 0;">Name: ${bankName}</p>
        <p style="margin: 2px 0;">Account No.: ${bankAccountNo}</p>
        <p style="margin: 2px 0;">IFSC code: ${bankIfsc}</p>
        <p style="margin: 2px 0;">Account Holder's Name: ${bankAccountHolder}</p>
    </div>

    <div style="width: 35%; padding: 8px; border-right: 1px solid #000;">
        <div style="background-color: #ff8c00; color: white; padding: 2px 5px; font-weight: bold; margin-bottom: 5px; display: inline-block; padding-bottom: 8px;">Terms and conditions</div>
        ${activeTerms.map(term => `
        <p style="margin: 2px 0;">${term}</p>
    `).join('')}
    </div>

    <div style="width: 30%; padding: 8px; text-align: center; display: flex; flex-direction: column; justify-content: center;">
        <p style="margin: 2px 0;">For: ${companyName}</p>
        
        ${signatureUrl
                    ? `<img src="${signatureUrl}" alt="Signature" style="max-width: 100%; max-height: 50px; margin: 10px auto; display: block;" crossorigin="anonymous" />`
                    : '<div style="height: 40px;"></div>'
                }
        
        <p style="margin: 2px 0; font-weight: bold;">Authorized Signatory</p>
    </div>

</div>

        </div>
      `;

            // Generate PDF from the container
            const canvas = await html2canvas(container, {
                scale: 3,
                useCORS: true,
                backgroundColor: "#ffffff",
                logging: false,
                windowWidth: 1200
            });

            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            const imgProps = pdf.getImageProperties(imgData);
            const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
            heightLeft -= pdfHeight;

            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
                heightLeft -= pdfHeight;
            }

            // Clean up
            document.body.removeChild(container);

            // Save PDF
            pdf.save(`Invoice_${invoice.invoice_number}.pdf`);

            toast.update(toastId, {
                render: "PDF Downloaded Successfully!",
                type: "success",
                isLoading: false,
                autoClose: 3000
            });

        } catch (err) {
            console.error("PDF Export Error:", err);
            toast.error("Failed to generate PDF");
        }
    };

    if (!invoice) {
        return null;
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Modal Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                    <h2 className="text-xl font-bold text-gray-800">Invoice Details</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <FiX size={24} />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="p-6">
                    <div className="space-y-6">
                        {/* Invoice Header Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h3 className="text-sm font-medium text-gray-500 mb-1">Invoice Number</h3>
                                <p className="text-lg font-semibold text-gray-900">{invoice.invoice_number}</p>
                            </div>
                            <div>
                                <h3 className="text-sm font-medium text-gray-500 mb-1">Invoice Date</h3>
                                <p className="text-lg font-semibold text-gray-900">
                                    {new Date(invoice.invoice_date).toLocaleDateString()}
                                </p>
                            </div>
                            <div>
                                <h3 className="text-sm font-medium text-gray-500 mb-1">Invoice Type</h3>
                                <div className="mt-1 text-sm text-gray-900">{invoice.invoice_type}</div>
                            </div>
                            <div>
                                <h3 className="text-sm font-medium text-gray-500 mb-1">Payment Status</h3>
                                <div className="mt-1 text-sm text-gray-900">{invoice.payment_status}</div>
                            </div>
                        </div>

                        <hr className="border-gray-200" />

                        {/* Customer Information */}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">Customer Information (Bill To)</h3>
                            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Name:</span>
                                    <span className="font-medium text-gray-900">{invoice.customer_name || 'N/A'}</span>
                                </div>
                                {invoice.company_name && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Company:</span>
                                        <span className="font-medium text-gray-900">{invoice.company_name}</span>
                                    </div>
                                )}
                                {invoice.customer_address && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Address:</span>
                                        <span className="font-medium text-gray-900 text-right max-w-[60%]">
                                            {[invoice.customer_address, invoice.customer_city, invoice.customer_state, invoice.customer_pincode]
                                                .filter(Boolean).join(', ')}
                                        </span>
                                    </div>
                                )}
                                {invoice.customer_gstin && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">GSTIN:</span>
                                        <span className="font-medium text-gray-900">{invoice.customer_gstin}</span>
                                    </div>
                                )}
                                {invoice.customer_state && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">State:</span>
                                        <span className="font-medium text-gray-900">{invoice.customer_state}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Order and Payment Status */}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">Status Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* <div className="bg-gray-50 rounded-lg p-4">
                                    <span className="text-sm text-gray-600">Order Status:</span>
                                    <p className="mt-1 font-medium text-gray-900 capitalize">
                                        <span className={`px-3 py-1 rounded-full text-sm ${invoice.order_status === 'pending' ? 'text-yellow-800' :
                                            invoice.order_status === 'completed' ? 'text-green-800' :
                                                invoice.order_status === 'cancelled' ? 'text-red-800' :
                                                    'text-gray-800'
                                            }`}>
                                            {invoice.order_status}
                                        </span>
                                    </p>
                                </div> */}
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <span className="text-sm text-gray-600">Payment Mode:</span>
                                    <p className="mt-1 font-medium text-gray-900">
                                        {invoice.payment_mode ? (
                                            <span className="capitalize">{invoice.payment_mode}</span>
                                        ) : (
                                            <span className="text-gray-400">Not specified</span>
                                        )}
                                    </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <span className="text-sm text-gray-600">Delivery Status:</span>
                                    <p className="mt-1 font-medium">
                                        <span className={`px-3 py-1 rounded-full text-sm ${invoice.delivery_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                            invoice.delivery_status === 'delivered' ? 'bg-green-100 text-green-800' :
                                                (invoice.delivery_status === 'returnable_basis' || invoice.delivery_status === 'failed') ? 'bg-red-100 text-red-800' :
                                                    'bg-gray-100 text-gray-800'
                                            }`}>
                                            {(invoice.delivery_status === 'returnable_basis' || invoice.delivery_status === 'failed') ? 'Returnable Basis' : (invoice.delivery_status ? invoice.delivery_status.charAt(0).toUpperCase() + invoice.delivery_status.slice(1) : 'Pending')}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Place of Supply Section */}
                        <hr className="border-gray-200" />
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">Place of Supply</h3>
                            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                                {invoice.supply_address && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Address:</span>
                                        <span className="font-medium text-gray-900">{invoice.supply_address}</span>
                                    </div>
                                )}
                                {invoice.supply_city && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">City:</span>
                                        <span className="font-medium text-gray-900">{invoice.supply_city}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span className="text-gray-600">State:</span>
                                    <span className="font-medium text-gray-900">
                                        {invoice.supply_state || invoice.customer_state || 'N/A'}
                                    </span>
                                </div>
                                {invoice.supply_pincode && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Pincode:</span>
                                        <span className="font-medium text-gray-900">{invoice.supply_pincode}</span>
                                    </div>
                                )}
                                {/* GST Type Indicator */}
                                <div className="flex justify-between pt-2 border-t border-gray-200">
                                    <span className="text-gray-600">GST Type:</span>
                                    {(() => {
                                        const supplyState = invoice.supply_state || invoice.customer_state || '';
                                        const companyStateVal = companySettings?.company_state || 'Delhi';
                                        const normalizeState = (s) => s ? (s.includes('-') ? s.split('-')[1].trim() : s.trim()).toLowerCase() : '';
                                        const isIntra = normalizeState(supplyState) === normalizeState(companyStateVal);
                                        return (
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                isIntra ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                            }`}>
                                                {isIntra ? 'Intra-State (CGST+SGST)' : 'Inter-State (IGST)'}
                                            </span>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>

                        {/* Items Table */}
                        {invoice.items && invoice.items.length > 0 && (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-3">Items ({invoice.items.length})</h3>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">HSN/SAC</th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Disc%</th>
                                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tax</th>
                                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {invoice.items.map((item, index) => (
                                                <tr key={index}>
                                                    <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        <div className="font-medium">{item.item_name || item.product_name || item.name || 'N/A'}</div>
                                                        {item.description && (
                                                            <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                                                        )}
                                                        {item.item_code && (
                                                            <p className="text-xs text-gray-400">Code: {item.item_code}</p>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-500">{item.hsn_sac || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 text-center">{item.quantity} {item.unit || ''}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 text-right">₹{parseFloat(item.rate || item.price || 0).toFixed(2)}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-500 text-right">
                                                        {parseFloat(item.discount || 0) > 0 ? `${parseFloat(item.discount).toFixed(2)}%` : '-'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-500 text-right">
                                                        ₹{parseFloat(item.tax_amount || 0).toFixed(2)}
                                                        {item.tax_rate && <span className="text-xs ml-1">({item.tax_rate}%)</span>}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">₹{parseFloat(item.amount || item.total || 0).toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="bg-gray-50">
                                            <tr>
                                                <td colSpan="3" className="px-4 py-3 text-sm font-semibold text-gray-800">Total</td>
                                                <td className="px-4 py-3 text-sm font-semibold text-gray-800 text-center">
                                                    {invoice.items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-800 text-right"></td>
                                                <td className="px-4 py-3 text-sm text-gray-800 text-right"></td>
                                                <td className="px-4 py-3 text-sm font-semibold text-gray-800 text-right">
                                                    ₹{invoice.items.reduce((sum, item) => sum + parseFloat(item.tax_amount || 0), 0).toFixed(2)}
                                                </td>
                                                <td className="px-4 py-3 text-sm font-bold text-blue-600 text-right">
                                                    ₹{invoice.items.reduce((sum, item) => sum + parseFloat(item.amount || item.total || 0), 0).toFixed(2)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Amount Summary */}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">Amount Summary</h3>
                            <div className="bg-gray-50 rounded-lg p-4">
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Subtotal:</span>
                                        <span className="font-medium text-gray-900">₹{parseFloat(invoice.subtotal).toFixed(2)}</span>
                                    </div>
                                    {invoice.tax_amount && parseFloat(invoice.tax_amount) > 0 && (
                                        <>
                                            {/* Determine GST type for display */}
                                            {(() => {
                                                const gstType = invoice.gst_type || (
                                                    (invoice.customer_state || invoice.state || '').toLowerCase().includes('delhi')
                                                        ? 'CGST_SGST' : 'IGST'
                                                );
                                                const taxAmt = parseFloat(invoice.tax_amount);
                                                if (gstType === 'CGST_SGST') {
                                                    return (
                                                        <>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">CGST (9%):</span>
                                                                <span className="font-medium text-gray-900">₹{(taxAmt / 2).toFixed(2)}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">SGST (9%):</span>
                                                                <span className="font-medium text-gray-900">₹{(taxAmt / 2).toFixed(2)}</span>
                                                            </div>
                                                        </>
                                                    );
                                                }
                                                return (
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">IGST (18%):</span>
                                                        <span className="font-medium text-gray-900">₹{taxAmt.toFixed(2)}</span>
                                                    </div>
                                                );
                                            })()}
                                            {/* GST Type Badge */}
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">GST Type:</span>
                                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                    (invoice.gst_type === 'CGST_SGST' ||
                                                     (!invoice.gst_type && (invoice.customer_state || invoice.state || '').toLowerCase().includes('delhi')))
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-blue-100 text-blue-800'
                                                }`}>
                                                    {(invoice.gst_type === 'CGST_SGST' ||
                                                      (!invoice.gst_type && (invoice.customer_state || invoice.state || '').toLowerCase().includes('delhi')))
                                                        ? 'Intra-State (CGST+SGST)'
                                                        : 'Inter-State (IGST)'}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                    {invoice.items && (() => {
                                        const totalDiscount = invoice.items.reduce((sum, item) => {
                                            const qty = parseFloat(item.quantity || 0);
                                            const rate = parseFloat(item.rate || item.price || 0);
                                            const disc = parseFloat(item.discount || 0);
                                            return sum + ((qty * rate * disc) / 100);
                                        }, 0);
                                        return totalDiscount > 0 ? (
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Discount:</span>
                                                <span className="font-medium text-red-600">- ₹{totalDiscount.toFixed(2)}</span>
                                            </div>
                                        ) : null;
                                    })()}
                                    <hr className="border-gray-300 my-2" />
                                    <div className="flex justify-between text-lg">
                                        <span className="font-semibold text-gray-800">Total Amount:</span>
                                        <span className="font-bold text-blue-600">₹{parseFloat(invoice.total_amount).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between mt-2">
                                        <span className="text-gray-600">Paid Amount:</span>
                                        <span className="font-medium text-green-600">₹{parseFloat(invoice.paid_amount || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between text-lg mt-2">
                                        <span className="font-semibold text-red-600">Due Amount:</span>
                                        <span className="font-bold text-red-600">₹{parseFloat(invoice.due_amount || (parseFloat(invoice.total_amount) - parseFloat(invoice.paid_amount || 0))).toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Notes */}
                        {invoice.notes && (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">Notes</h3>
                                <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4">
                                    <p className="text-gray-700">{invoice.notes}</p>
                                </div>
                            </div>
                        )}

                        {/* Terms and Conditions */}
                        {invoice.terms && (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">Terms & Conditions</h3>
                                <div className="bg-blue-50 border-l-4 border-blue-400 rounded-lg p-4">
                                    <p className="text-gray-700">{invoice.terms}</p>
                                </div>
                            </div>
                        )}

                        {/* Authorized Signatory */}
                        {companySettings?.signature_image && (
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">Authorized Signatory</h3>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <img
                                        src={`${API_URL.replace('/api', '')}${companySettings.signature_image}`}
                                        alt="Authorized Signatory"
                                        className="max-w-xs max-h-24 object-contain"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Metadata */}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">Additional Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <span className="text-sm text-gray-600">Created By:</span>
                                    <p className="mt-1 font-medium text-gray-900">{invoice.created_by_name || `User ID: ${invoice.created_by}`}</p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <span className="text-sm text-gray-600">Created At:</span>
                                    <p className="mt-1 font-medium text-gray-900">
                                        {new Date(invoice.created_at).toLocaleDateString()}
                                    </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <span className="text-sm text-gray-600">Last Updated:</span>
                                    <p className="mt-1 font-medium text-gray-900">
                                        {new Date(invoice.updated_at).toLocaleDateString()}
                                    </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <span className="text-sm text-gray-600">Invoice ID:</span>
                                    <p className="mt-1 font-medium text-gray-900">{invoice.id}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Modal Footer */}
                <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                        Close
                    </button>
                    <button
                        onClick={downloadPDF}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                        <FiDownload size={16} />
                        Download PDF
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InvoiceModal;